import { useState } from 'react'
import { Link, useFetcher, useSubmit } from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { HoldToConfirmButton } from '#app/components/HoldToConfirmButton.tsx'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { Separator } from '#app/components/ui/separator'
import { formatHours } from '#app/utils/misc'
import { useOptionalAdminUser, useOptionalUser } from '#app/utils/user'
import {
	backgroundColor,
	borderColor,
	type DutyType,
	type UserScheduleType,
} from '#app/utils/user-schedule.ts'
import { type Route } from './+types/timeline.ts'
import {
	type ScheduleType,
	type PositionDitchType,
} from './timeline.server.tsx'

export { loader, action } from './timeline.server.tsx'

type TimelineCell =
	| { kind: 'user'; user: UserScheduleType }
	| { kind: 'duty'; duty: DutyType }

// flatten a section column into an ordered list, inserting each duty as its own
// cell directly above the irrigator it belongs to
function buildColumn(
	section: PositionDitchType[string][string] | undefined,
	entry: string,
): TimelineCell[] {
	const cells: TimelineCell[] = []
	if (!section) return cells
	for (const row of Object.keys(section)) {
		const user = section[row]?.[entry]
		if (!user) continue
		for (const duty of user.duties ?? []) cells.push({ kind: 'duty', duty })
		cells.push({ kind: 'user', user })
	}
	return cells
}

export default function TimelineRoute({ loaderData }: Route.ComponentProps) {
	const user = useOptionalUser()
	const userIsAdmin = useOptionalAdminUser()

	const [showButtons, setShowButtons] = useState(false)

	const defaultUserEntry = user?.ports?.[0]?.entry
	const { status, schedule, users } = loaderData
	const { id: scheduleId, date: scheduleDate } = schedule

	if (status !== 'idle' || !scheduleId || !users || !Object.keys(users).length)
		return null

	return (
		<div className="text-align-webkit-center bg-background flex w-full flex-col items-center justify-center gap-1">
			<div className="my-4 flex flex-col items-center justify-center gap-6">
				<h1 className="text-h1 text-center">
					Irrigation Timeline for {scheduleDate}
				</h1>
				<h2 className="text-h2 text-center">
					{schedule.scheduleDates.join(' ─ ')}
				</h2>
				<h3 className="text-h3 capitalize">
					Source: {schedule.source} | Cost Per Hour: ${schedule.costPerHour}
				</h3>
			</div>
			<div className="flex w-full flex-row-reverse flex-wrap gap-2 p-0.5 pr-6">
				<Button>
					<Link reloadDocument to={`/resources/download/print/${scheduleDate}`}>
						<Icon name="download">Download CSV</Icon>
					</Link>
				</Button>
				{userIsAdmin && (
					<Button onClick={() => setShowButtons(!showButtons)}>
						{`${showButtons ? '▲ Hide' : '▼ Show'} Email Buttons`}
					</Button>
				)}
			</div>
			<div className="text-align-webkit-center bg-background flex w-full flex-col items-center justify-center gap-1">
				<div className="m-auto w-full" style={{ height: 'fill-available' }}>
					<OneColumn
						schedule={schedule}
						users={users}
						defaultUserEntry={defaultUserEntry}
						showButtons={showButtons}
					/>
					<TwoColumns
						schedule={schedule}
						users={users}
						showButtons={showButtons}
					/>
				</div>
			</div>
		</div>
	)
}

function OneColumn({
	schedule,
	users,
	defaultUserEntry,
	showButtons,
}: {
	schedule: ScheduleType
	users: PositionDitchType
	defaultUserEntry?: string | null
	showButtons: boolean
}) {
	const [visible, setVisible] = useState(defaultUserEntry ?? '10-01')
	const handleToggleVisible = (value: string) => setVisible(value)
	return (
		<div className="m-auto block lg:hidden">
			<div className="flex w-full flex-row justify-center">
				<Button
					variant="outline-link"
					className={`mx-0.5 my-1 ${visible === '10-01' && 'bg-secondary underline underline-offset-4'}`}
					onClick={() => handleToggleVisible('10-01')}
				>
					[10-01] (Ditches 1-4)
				</Button>
				<Button
					variant="outline-link"
					className={`mx-0.5 my-1 ${visible === '10-03' && 'bg-secondary underline underline-offset-4'}`}
					onClick={() => handleToggleVisible('10-03')}
				>
					[10-03] (Ditches 5-8)
				</Button>
			</div>
			<div className="flex w-full flex-row justify-center">
				{Array.from(
					{ length: 4 },
					(_, i) => i + (visible === '10-01' ? 1 : 5),
				).map((i) => (
					<Button
						variant="outline-link"
						key={`#jump${i}`}
						asChild
						className="mx-0.5 my-1"
					>
						<Link to={`#d${i}`}>Ditch {i}</Link>
					</Button>
				))}
			</div>
			{Object.keys(users).map((page) => (
				<>
					<div id={`d${page === '0' ? 9 : Number(page)}`}></div>
					<div id={`d${page === '0' ? 9 : Number(page) + 4}`}></div>
					<table className="w-full table-fixed lg:w-full">
						<thead>
							<tr>
								<th className="bg-primary text-body-lg text-secondary rounded-md text-center">
									{`[${visible}] Ditch ${page === '0' ? 9 : Number(page) + (visible === '10-01' ? 0 : 4)}`}
								</th>
							</tr>
						</thead>
						{users[page] &&
							Object.keys(users[page]).map((section, index) => {
								const cells = buildColumn(users[page]?.[section], visible)
								return (
									<tbody key={`${page}-${section}`}>
										{cells.map((cell, i) => (
											<tr className="w-full" key={`${section}-${i}`}>
												<td className="w-[50%] p-0.5">
													{cell.kind === 'user' ? (
														<UserCard
															schedule={schedule}
															user={cell.user}
															showButtons={showButtons}
														/>
													) : (
														<DutyCard
															duty={cell.duty}
															scheduleDate={schedule.date}
														/>
													)}
												</td>
											</tr>
										))}
										{index === 0 ? (
											<tr>
												<td
													className="border-t-2 border-dashed border-sky-950"
													key={`${page}-${visible}-${section}`}
												></td>
											</tr>
										) : null}
									</tbody>
								)
							})}
					</table>
					<Separator className="mt-4 mb-8" />
				</>
			))}
		</div>
	)
}

function TwoColumns({
	schedule,
	users,
	showButtons,
}: {
	schedule: ScheduleType
	users: PositionDitchType
	showButtons: boolean
}) {
	return (
		<div className="m-auto block max-lg:hidden">
			{Array.from({ length: 8 }, (_, i) => i + 1).map((i) => (
				<Button
					variant="outline-link"
					key={`#jump${i}`}
					asChild
					className="mx-0.5 my-1"
				>
					<Link to={`#ditch${i}`}>Ditch {i}</Link>
				</Button>
			))}
			{Object.keys(users).map((page) => (
				<div key={`${page}`} className="m-auto block max-lg:hidden">
					<div id={`ditch${page === '0' ? 9 : Number(page)}`}></div>
					<div id={`ditch${page === '0' ? 9 : Number(page) + 4}`}></div>
					<table className="mb-2 w-full table-fixed lg:w-[90%]">
						<thead>
							<tr>
								<th className="bg-primary text-body-lg text-secondary rounded-md text-center">
									[10-01] Ditch {page === '0' ? 9 : Number(page)}
								</th>
								<th className="bg-primary text-body-lg text-secondary rounded-md text-center">
									[10-03] Ditch {page === '0' ? 9 : Number(page) + 4}
								</th>
							</tr>
						</thead>
						{users[page] &&
							Object.keys(users[page]).map((section, index) => {
								const leftCells = buildColumn(users[page]?.[section], '10-01')
								const rightCells = buildColumn(users[page]?.[section], '10-03')
								const rowCount = Math.max(leftCells.length, rightCells.length)
								return (
									<tbody key={`${page}-${section}`}>
										{Array.from({ length: rowCount }).map((_, i) => {
											const left = leftCells[i]
											const right = rightCells[i]
											return (
												<tr className="w-full" key={`${page}-${section}-${i}`}>
													<td className="w-[50%] p-0.5">
														{left ? (
															left.kind === 'user' ? (
																<UserCard
																	schedule={schedule}
																	user={left.user}
																	showButtons={showButtons}
																/>
															) : (
																<DutyCard
																	duty={left.duty}
																	scheduleDate={schedule.date}
																/>
															)
														) : null}
													</td>
													<td className="w-[50%] p-0.5">
														{right ? (
															right.kind === 'user' ? (
																<UserCard
																	schedule={schedule}
																	user={right.user}
																	showButtons={showButtons}
																/>
															) : (
																<DutyCard
																	duty={right.duty}
																	scheduleDate={schedule.date}
																/>
															)
														) : null}
													</td>
												</tr>
											)
										})}
										{index === 0 ? (
											<tr>
												<td
													className="border-t-2 border-dashed border-sky-950"
													key={`${page}-10-01-${section}`}
												></td>
												<td
													className="border-t-2 border-dashed border-sky-950"
													key={`${page}-10-03-${section}`}
												></td>
											</tr>
										) : null}
									</tbody>
								)
							})}
					</table>
					<Separator className="mt-4 mb-8" />
				</div>
			))}
		</div>
	)
}

const dutyBackground = (duty: string) => {
	switch (duty) {
		case 'first':
			return 'border-green-700/40 bg-green-100 text-green-950'
		case 'crossover':
			return 'border-blue-700/40 bg-blue-100 text-blue-950'
		case 'last':
			return 'border-red-700/40 bg-red-100 text-red-950'
		default:
			return 'border-secondary-foreground/40 bg-muted text-foreground'
	}
}

const acknowledgedStatusText = (acknowledged?: boolean | null) => {
	if (acknowledged) return 'Acknowledged'
	if (acknowledged === false) return 'Requests Help'
	return 'No Response'
}

const acknowledgedStatusColor = (acknowledged?: boolean | null) => {
	if (acknowledged) return 'font-semibold text-green-700'
	if (acknowledged === false) return 'font-semibold text-red-700'
	return 'font-semibold text-yellow-700'
}

function DutyCard({
	duty,
	scheduleDate,
}: {
	duty: DutyType
	scheduleDate: string
}) {
	return (
		<div
			className={`flex min-h-[3rem] flex-col justify-center rounded-lg border-2 p-2 text-left ${dutyBackground(duty.duty)}`}
		>
			<div className="text-body-sm flex flex-row flex-wrap justify-between gap-1">
				<span>
					<span className="font-semibold capitalize">{duty.duty}:</span>{' '}
					{duty.dutyDetail}
				</span>
				{duty.dutyStartString ? (
					<span className="min-w-[140px] text-right text-nowrap">
						{duty.dutyStartString}
					</span>
				) : null}
			</div>
			<div className="flex flex-row flex-wrap items-center justify-between gap-1">
				{duty.acknowledged !== true && duty.volunteer ? (
					<div className="text-body-xs">
						{duty.volunteer}
						{' — '}
						<span className="font-semibold text-blue-700">Volunteer</span>
					</div>
				) : duty.member ? (
					<div className="text-body-xs">
						{duty.member}
						{' — '}
						<span className={acknowledgedStatusColor(duty.acknowledged)}>
							{acknowledgedStatusText(duty.acknowledged)}
						</span>
					</div>
				) : (
					<span />
				)}
				<DutyAction duty={duty} scheduleDate={scheduleDate} />
			</div>
		</div>
	)
}

// single most-relevant crossover action for the current user, submitted to the
// crossovers route action; renders nothing when there's no action to offer
function DutyAction({
	duty,
	scheduleDate,
}: {
	duty: DutyType
	scheduleDate: string
}) {
	const user = useOptionalUser()
	const fetcher = useFetcher()
	const {
		crossoverId,
		isCurrentUser,
		isCurrentVolunteer,
		acknowledged,
		volunteer,
		isLocked,
	} = duty

	let intent: string | null = null
	let label = ''
	let border = ''
	if (isCurrentUser && !acknowledged) {
		intent = 'acknowledge'
		label = 'Acknowledge'
		border = 'border-green-700'
	} else if (isCurrentVolunteer) {
		intent = 'unvolunteer'
		label = 'Remove Volunteer'
		border = 'border-red-700'
	} else if (user && !isCurrentUser && !volunteer && acknowledged !== true) {
		intent = 'volunteer'
		label = 'Volunteer to Help'
		border = 'border-blue-700'
	}

	if (!intent) return null

	const pending = fetcher.state !== 'idle'
	return (
		<fetcher.Form method="POST" action={`/schedule/${scheduleDate}/crossovers`}>
			<input type="hidden" name="crossoverId" value={crossoverId} />
			{intent === 'volunteer' && user ? (
				<input type="hidden" name="volunteerId" value={user.id} />
			) : null}
			<Button
				type="submit"
				name="intent"
				value={intent}
				variant="outline"
				disabled={isLocked || pending}
				className={`text-primary h-7 border-2 shadow-sm shadow-gray-700 ${border}`}
			>
				{label}
			</Button>
		</fetcher.Form>
	)
}

function UserCard({
	schedule,
	user,
	showButtons,
}: {
	schedule: ScheduleType
	user: UserScheduleType
	showButtons: boolean
}) {
	const {
		display,
		hours,
		address,
		userSchedule,
		first,
		crossover,
		last,
		emailed,
	} = user
	return (
		<div
			className={`flex min-h-[3rem] flex-col justify-center rounded-lg ${hours ? 'border-secondary-foreground bg-muted border' : 'bg-muted-40 border border-gray-700'} p-2 ${borderColor({ first, crossover, last })}`}
		>
			{first || crossover || last ? (
				<div
					key="badges"
					className="mt-[-20px] ml-2 flex flex-row justify-end gap-1"
				>
					{first && (
						<Badge
							className={`font-semibold text-white capitalize ${backgroundColor('first')}`}
							variant="outline"
						>
							{'First'}
						</Badge>
					)}
					{crossover && (
						<Badge
							className={`font-semibold text-white capitalize ${backgroundColor('crossover')}`}
							variant="outline"
						>
							{'Crossover'}
						</Badge>
					)}
					{last && (
						<Badge
							className={`font-semibold text-white capitalize ${backgroundColor('last')}`}
							variant="outline"
						>
							{'Last'}
						</Badge>
					)}
				</div>
			) : null}
			<div className="flex flex-row">
				<div
					key="address_name_hours"
					className="flex w-1/2 flex-col flex-wrap gap-2 md:flex-row"
				>
					<div key="address_name" className="flex flex-row">
						<span
							className="text-body-sm min-w-[60px] overflow-hidden text-right text-nowrap"
							style={{ direction: 'rtl' }}
						>
							&nbsp;:{address}
						</span>
						<span className="text-body-sm min-w-[100px] text-left text-nowrap text-ellipsis">
							{display}
						</span>
					</div>
					<div key="hours" className="text-body-sm ml-[60px] min-w-[40px]">
						{formatHours(Number(hours))}
					</div>
				</div>
				<div
					key="start-stop"
					className="flex w-1/2 flex-col justify-end gap-2 md:flex-row"
				>
					{userSchedule &&
						userSchedule.map((row, r) => (
							<span
								key={`row-${r}`}
								className="text-body-sm min-w-[80px] overflow-hidden text-ellipsis md:w-1/2"
							>
								{row}
							</span>
						))}
				</div>
				<EmailStatusButtons
					user={user}
					schedule={schedule}
					hours={hours}
					emailed={emailed}
					showButtons={showButtons}
				/>
			</div>
		</div>
	)
}
function EmailStatusButtons({
	user: { userId, primaryEmail, primaryOptIn },
	schedule: { id: scheduleId, date },
	hours,
	emailed,
	showButtons,
}: {
	user: UserScheduleType
	schedule: ScheduleType
	hours: number | bigint | null
	emailed?: boolean | null
	showButtons: boolean
}) {
	const submit = useSubmit()
	const handleSendEmailClicked = async () => {
		await submit(
			{ intent: 'email-schedule', date, userId, scheduleId },
			{ method: 'POST' },
		)
	}

	const color =
		primaryEmail && primaryOptIn ? (emailed ? 'green' : 'blue') : 'red'
	const textColor = `text-${color}-700`
	const borderColor = `border-${color}-700`
	const icon = (
		<Icon
			name={emailed ? 'envelope-open' : 'envelope-closed'}
			fill="currentColor"
			className={`size-5 ${textColor}`}
		/>
	)
	// if (!hours) return null
	if (hours && showButtons) {
		return (
			<div className="flex items-center">
				<HoldToConfirmButton
					variant="outline"
					disabled={!primaryEmail || !primaryOptIn}
					className={`rounded-md border ${borderColor} ${textColor}`}
					onSubmit={handleSendEmailClicked}
				>
					{icon}
				</HoldToConfirmButton>
			</div>
		)
	}
	return null
}

export const meta: Route.MetaFunction = ({ params }) => {
	return [
		{ title: `Irrigation Timeline | ${params.date}` },
		{
			name: 'description',
			content: `Clearwater Farms 1 Irrigation Timeline`,
		},
	]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
